import { useEffect, useRef } from 'react';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import { NsColumn } from '@britishgasenergy/react-nucleus';

import { NsContent } from 'components/common/ns-content/NsContent';
import { NsPanel } from 'components/common/ns-panel/NsPanel';
import { NsTitle } from 'components/common/ns-title/NsTitle';
import { AddressPlayback } from 'components/direct-sales/select-options/components/AddressPlayback';
import { EligibleFuelCard } from 'components/direct-sales/select-options/components/EligibleFuelCard';
import { selectedAddress } from 'state/selected-address/selected-address-slice';
import { ChangeTariffCard } from 'components/direct-sales/select-options/components/ChangeTariffCard';
import { UnmeteredCard } from 'components/direct-sales/select-options/components/UnmeteredCard';
import { HomeMoveCard } from 'components/direct-sales/select-options/components/HomeMoveCard';
import { BgLiteCard } from 'components/direct-sales/select-options/components/BgLiteCard';
import { DeEnergisedCard } from './components/DeEnergisedCard';
import { RelatedMeterCard } from 'components/direct-sales/select-options/components/RelatedMeterCard';
import { useMeterExceptions } from 'components/direct-sales/select-options/hooks/useMeterExceptions';
import { useMeterType } from 'hooks/useMeterType';
import { useIsExistingCustomer } from 'components/direct-sales/select-options/hooks/useIsExistingCustomer';
import { FUEL_OPTIONS } from 'config/app-config';
import { selectHistory } from 'state/journey-routes/journey-routes-slice';
import { checkMatchPath } from 'utils/misc';
import { routePath } from 'config/route-config';
import { setInitialState as resetFuelType } from 'state/fuel-type/fuel-type-slice';
import { pushEvent } from 'state/analytics/analytics-slice';
import { CardsContext } from 'components/direct-sales/select-options/contexts/CardContext';
import { selectFuelType } from 'state/fuel-type/fuel-type-slice';

export const testId = 'selectOptionsTestId';

const dataMapping = [
  {
    gas: true,
    electricity: false,
    fuelName: FUEL_OPTIONS.GAS
  },
  {
    gas: false,
    electricity: true,
    fuelName: FUEL_OPTIONS.ELECTRIC
  },
  {
    gas: true,
    electricity: true,
    fuelName: 'energy'
  }
];

const getData = ({ gas, electricity }) => {
  return (
    dataMapping.find(
      (data) => data.gas === gas && data.electricity === electricity
    ) ?? {}
  );
};

export const SelectOptions = () => {
  const dispatch = useDispatch();
  const cardsRef = useRef([]);
  const { errors, hasMeterExceptions } = useMeterExceptions();
  const address = useSelector(selectedAddress, shallowEqual);
  const {
    isExistingCustomer,
    isExistingNEPCustomer,
    isExistingBGCustomer
  } = useIsExistingCustomer({ errors });
  const existingFuel = getData({
    gas: isExistingNEPCustomer.gas || isExistingBGCustomer.gas,
    electricity: isExistingNEPCustomer.electric || isExistingBGCustomer.electric
  }).fuelName;
  const historyStack = useSelector(selectHistory, shallowEqual);
  const hasVisitedFuelTypePage = !!historyStack.find((path) =>
    checkMatchPath(path, routePath.fuelType)
  );
  const fuelType = useSelector(selectFuelType, shallowEqual);
  const isElecOnly = fuelType.electric && !fuelType.gas;

  const { isRelatedElectricityMeter } = useMeterType();
  const registerCard = (card) =>
    (cardsRef.current = Array.from(new Set([...cardsRef.current, card])));

  useEffect(() => {
    // back navigation reset fuel type in case it was not set by fuelType page (meter validation)
    // to show same errors when returning to select options page
    !hasVisitedFuelTypePage && dispatch(resetFuelType());

    hasMeterExceptions &&
      dispatch(
        pushEvent({
          category: 'error',
          status: errors
            .reduce((acc, error) => {
              return [...acc, `${error?.code}:${error?.keyword}`];
            }, [])
            .join(';')
        })
      );

    dispatch(
      pushEvent({
        category: 'nep_aquisition',
        action: 'impression',
        label: cardsRef.current.join(';')
      })
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const relatedMeterTitle =
    isRelatedElectricityMeter && isElecOnly
      ? `We noticed you have a related meter`
      : `Join the UK's longest providing energy supplier.`;

  const title = isExistingCustomer
    ? `We already supply ${existingFuel} to this address`
    : relatedMeterTitle;

  return (
    <NsPanel testId={testId}>
      <NsContent>
        <NsTitle title={title} data-testid="selectOptionsTitle" />
      </NsContent>
      <AddressPlayback address={address} />
      <NsColumn hockeypitch="3">
        <CardsContext.Provider value={{ registerCard }}>
          <ChangeTariffCard errors={errors} name="changeTariff" />
          <HomeMoveCard errors={errors} name="homeMove" />
          <EligibleFuelCard name="eligibleFuel" />
          <UnmeteredCard errors={errors} name="unmetered" />
          <BgLiteCard errors={errors} name="bgLite" />
          <DeEnergisedCard errors={errors} name="deEnergised" />
          <RelatedMeterCard errors={errors} name="relatedMeter" />
        </CardsContext.Provider>
      </NsColumn>
    </NsPanel>
  );
};

export default SelectOptions;
